import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import './StationSelector.css';

/**
 * Station selector — sits in the top header.
 *
 * Shows the user's currently active station with a chevron. Clicking opens a
 * dropdown listing all of their APPROVED stations (the base is marked with a
 * "Base" badge). Selecting one calls setCurrentStation() and reloads the page
 * so every module's data refetches in the new station's scope.
 *
 * Pending and Rejected stations are shown but are not selectable — they are
 * displayed lower in the list with a status badge so the user knows their
 * registration is in flight.
 */
export default function StationSelector() {
  const { user, currentStationId, setCurrentStation } = useAuth();
  const [open, setOpen]   = useState(false);
  const [list, setList]   = useState([]);
  const [loading, setL]   = useState(false);
  const ref = useRef(null);

  /* ── Load stations once per mount ── */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setL(true);
    api.stations.mine()
      .then(rows => { if (!cancelled) setList(Array.isArray(rows) ? rows : []); })
      .catch(()   => { if (!cancelled) setList([]); })
      .finally(() => { if (!cancelled) setL(false); });
    return () => { cancelled = true; };
  }, [user]);

  /* ── Close on outside click ── */
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!user) return null;
  // Sub-users are locked to their own station — only the account owner switches.
  if (user.actor_type === 'user') return null;

  const approved = list.filter(s => s.status === 'Approved' && Number(s.is_active) === 1);
  const pending  = list.filter(s => s.status === 'Pending');
  const rejected = list.filter(s => s.status === 'Rejected');

  const current = approved.find(s => s.id === currentStationId)
                || approved.find(s => Number(s.is_base) === 1)
                || approved[0]
                || null;

  const pick = (s) => {
    if (s.status !== 'Approved' || Number(s.is_active) !== 1) return;
    if (s.id === currentStationId) { setOpen(false); return; }
    setCurrentStation(s.id);
    setOpen(false);
    // Force a fresh fetch of every module by reloading. Simpler and safer
    // than per-hook invalidation across 30+ modules.
    setTimeout(() => window.location.reload(), 50);
  };

  return (
    <div className="station-selector" ref={ref}>
      <button className="station-selector-button" onClick={() => setOpen(!open)} type="button">
        <span className="ss-icon">⛽</span>
        <span className="ss-name" title={current?.name}>
          {loading ? 'Loading…' : (current?.name || 'No station')}
        </span>
        {current && Number(current.is_base) === 1 && <span className="ss-badge ss-base">Base</span>}
        <span className="ss-chev">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="station-selector-menu">
          {approved.length > 0 && (
            <>
              <div className="ss-section-label">Active stations</div>
              {approved.map(s => (
                <button
                  key={s.id}
                  className={`ss-item ${s.id === currentStationId ? 'ss-active' : ''}`}
                  onClick={() => pick(s)}
                  type="button"
                >
                  <span className="ss-item-name">{s.name}</span>
                  {Number(s.is_base) === 1 && <span className="ss-badge ss-base">Base</span>}
                  {s.id === currentStationId && <span className="ss-check">✓</span>}
                </button>
              ))}
            </>
          )}

          {pending.length > 0 && (
            <>
              <div className="ss-section-label">Pending approval</div>
              {pending.map(s => (
                <div key={s.id} className="ss-item ss-disabled" title="Awaiting Noris team approval">
                  <span className="ss-item-name">{s.name}</span>
                  <span className="ss-badge ss-pending">Pending</span>
                </div>
              ))}
            </>
          )}

          {rejected.length > 0 && (
            <>
              <div className="ss-section-label">Rejected</div>
              {rejected.map(s => (
                <div key={s.id} className="ss-item ss-disabled" title={s.decision_note || 'Rejected'}>
                  <span className="ss-item-name">{s.name}</span>
                  <span className="ss-badge ss-rejected">Rejected</span>
                </div>
              ))}
            </>
          )}

          <div className="ss-divider" />
          <button className="ss-item ss-action"
                  onClick={() => { setOpen(false); window.location.hash = 'RegisterStation'; }}
                  type="button">
            <span className="ss-item-icon">＋</span>
            <span className="ss-item-name">Register New Station</span>
          </button>
        </div>
      )}
    </div>
  );
}
